// src/components/ui/profile/profile-card.tsx
"use client"

import { Button } from "@/components/ui/button"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Link } from "react-router"
import { GraduationCap } from "lucide-react"
import { useThemeStore } from "@/hooks/stores/themeStore"

interface ProfileCardProps {
  user?: {
    name?: string;
    grade?: string;
    email?: string;
    avatarUrl?: string;
    level?: number;
    totalPoints?: number;
    streakDays?: number;
  };
  onViewProfile?: () => void;
}

export function ProfileCard({
  user = {
    name: "Student",
    grade: "Grade 7",
    email: "student@example.com",
    avatarUrl: "",
    level: 1,
    totalPoints: 0,
    streakDays: 0,
  },
  onViewProfile,
}: ProfileCardProps) {
  const currentTheme = useThemeStore((state) => state.getCurrentTheme());

  // Generate avatar fallback initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get avatar URL or use fallback
  const avatarUrl = user?.avatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.name || 'student'}`;

  return (
    <div
      className="p-6 rounded-2xl text-center shadow-sm"
      style={{
        background: `linear-gradient(135deg, ${currentTheme.colors.muted}, ${currentTheme.colors.card})`,
        border: `1px solid ${currentTheme.colors.border}`
      }}
    >
      {/* Avatar with level badge */}
      <div className="relative w-24 h-24 mx-auto mb-4">
        <Avatar
          className="w-full h-full border-4"
          style={{ borderColor: 'var(--theme-background)' }}
        >
          <AvatarImage
            src={avatarUrl}
            alt={user?.name || "Student"}
            className="object-cover"
          />
          <AvatarFallback
            className="text-2xl font-bold"
            style={{
              background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`,
              color: 'white'
            }}
          >
            {getInitials(user?.name || "Student")}
          </AvatarFallback>
        </Avatar>

        {/* Level badge */}
        <div
          className="absolute -bottom-2 -right-2 rounded-full w-10 h-10 flex items-center justify-center border-2 shadow-md"
          style={{
            backgroundColor: currentTheme.colors.primary,
            color: 'white',
            borderColor: 'var(--theme-background)'
          }}
        >
          <span className="font-bold text-sm">L{user?.level || 1}</span>
        </div>
      </div>

      {/* Name and Grade */}
      <h3
        className="font-bold text-xl mb-1"
        style={{ color: 'var(--theme-foreground)' }}
      >
        {user?.name || "Student"}
      </h3>

      <div
        className="flex items-center justify-center gap-2 mb-3"
        style={{ color: 'var(--theme-foreground)', opacity: 0.7 }}
      >
        <GraduationCap className="w-4 h-4" />
        <p>
          {user?.grade || "Grade 7"}
        </p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div
          className="p-3 rounded-lg"
          style={{
            backgroundColor: `${currentTheme.colors.primary}15`,
            backdropFilter: 'blur(10px)'
          }}
        >
          <p
            className="text-2xl font-bold"
            style={{ color: currentTheme.colors.primary }}
          >
            {user?.totalPoints || 0}
          </p>
          <p
            className="text-xs"
            style={{ color: 'var(--theme-foreground)', opacity: 0.7 }}
          >
            Points
          </p>
        </div>
        <div
          className="p-3 rounded-lg"
          style={{
            backgroundColor: `${currentTheme.colors.secondary}15`,
            backdropFilter: 'blur(10px)'
          }}
        >
          <p
            className="text-2xl font-bold"
            style={{ color: currentTheme.colors.secondary }}
          >
            {user?.streakDays || 0}
          </p>
          <p
            className="text-xs"
            style={{ color: 'var(--theme-foreground)', opacity: 0.7 }}
          >
            Day Streak
          </p>
        </div>
      </div>

      {/* View Profile Button */}
      <Link to="/profile" className="w-full block">
        <Button
          onClick={onViewProfile}
          className="w-full font-semibold py-3 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg hover:scale-[1.02]"
          style={{
            backgroundColor: currentTheme.colors.primary,
            color: 'white'
          }}
        >
          View Full Profile
        </Button>
      </Link>

      {/* Email (small) */}
      <p
        className="text-xs mt-4 truncate"
        style={{ color: 'var(--theme-foreground)', opacity: 0.5 }}
      >
        {user?.email || "student@example.com"}
      </p>
    </div>
  )
}